import React, { useEffect, useCallback } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { YMInitializer } from 'react-yandex-metrika';
import smoothscroll from 'smoothscroll-polyfill';

import { capturePosthogEvent, posthogEvents } from '../utils/posthog';

import { USE_YA, YANDEX_METRIC_ID } from '../constants';
import { VISITOR_COOKIE_PARAMS } from '../api/config';
import { getCookie, setCookie, setCustomVh, storageCreator, deleteCookie } from '../utils';

import {
  metaActions,
  messagesActions,
  expertsActions,
  checkoutActions,
  dialogsActions,
  environmentActions
} from '../store/actions';
import {
  getCurrentDialogId,
  getIsBigTabletAndDesktop, 
  getIsDialogStatusChanged, 
  getIsMobile, 
  getIsUserAuthenticated, 
  getPhoneNumber, 
  getUserNewOrderCount, 
  socketIsConnectedSelector 
} from '../store/selectors';
import { TABLET_BREAKPOINT } from '../constants/breakpoints';
import { SAVED_LAYOUT_VALUE_KEY } from '../constants/storageKeys';

smoothscroll.polyfill();

const SubscriptionsContainer = ({
  children,
  isSocketConnected,
  openSocket,
  reconnectSocket,
  listenUserAuth,
  listenRecentDialog,
  listenExpertJoin,
  addMessagesSubscription,
  expertTypingSubscription,
  addEcommerceSubscription,
  expertStatusChangedSubscription,
  messageChangedSubscription,
  listenPhoneRequestAuthFailed,
  listDialogStatusChanged,
  listenUnreadCount,
  listenUserNewOrder,
  getDialogs,
  fetchOrders,
}) => {
  const dispatch = useDispatch();
  const userNewOrderCount = useSelector(getUserNewOrderCount);
  const mobileLayoutValue = sessionStorage.getItem(SAVED_LAYOUT_VALUE_KEY);
  const isDialogStatusChanged = useSelector(getIsDialogStatusChanged);
  const isMobile = useSelector(getIsMobile);
  const isBigTabletAndDesktop = useSelector(getIsBigTabletAndDesktop);
  const phoneNumber = useSelector(getPhoneNumber);
  const isAuthenticated = useSelector(getIsUserAuthenticated);
  const visitorIdCookie = getCookie(VISITOR_COOKIE_PARAMS.NAME);
  const { visitorId, dialogId, senderId } = useSelector((state) => state.meta.user);
  const currentDialogId = useSelector(getCurrentDialogId);
  const { phone, code, logout } = useSelector((state) => state.meta.auth);

  const sendVisitorIdToHost = (value) => {
    const { parent } = window.frames;
    if (parent) {
      const visitorIdAction = { type: 'visitorId', payload: value };
      parent.postMessage(visitorIdAction, '*');
    }
  };

  const removeExpert = () => {
    dispatch(expertsActions.changeExpert(dialogId, senderId, '', ''));
  };

  useEffect(() => {
    setCustomVh();
    window.addEventListener('resize', () => {
      // Adds listener to resize for custom vh recalculation. Needed to reflow layout after resizing/turn mobile device.
      setCustomVh();
    });
    listenUserAuth();
  }, [listenUserAuth]);

  useEffect(() => {
    listenRecentDialog();
    listDialogStatusChanged();
  }, [listenRecentDialog, listDialogStatusChanged]);

  useEffect(() => {
    if (visitorId){
      getDialogs();
    }

    if (isDialogStatusChanged) {
      dispatch(dialogsActions.setDialogStatus(false));
    }
  }, [isDialogStatusChanged]);

  const changeSearchPlaceholder = useCallback(() => {
    dispatch(environmentActions.setIsBigTabletAndDesktop(window.innerWidth >= TABLET_BREAKPOINT));
    dispatch(environmentActions.setIsMobile(window.innerWidth < TABLET_BREAKPOINT));
  }, [isBigTabletAndDesktop, isMobile]);

  useEffect(() => {
    window.addEventListener('resize', changeSearchPlaceholder);

    return () => {
      window.removeEventListener('resize', changeSearchPlaceholder);
    };
  }, [isBigTabletAndDesktop, isMobile]);

  useEffect(() => {
    if (!isSocketConnected) {
      openSocket();
    } else {
      listenExpertJoin();
    }
  }, [isSocketConnected, openSocket, listenExpertJoin]);

  useEffect(() => {
    if (isSocketConnected) {
      if (phone && code) {
        reconnectSocket({ phone: phone, code: code, logout: null });
      }
    }
  }, [phone, code, reconnectSocket]);

  useEffect(() => {
   if (visitorId){
    getDialogs();
   }
    
  }, [visitorId]);

  useEffect(() => {
    if (isAuthenticated) {
      capturePosthogEvent(posthogEvents.AUTH_LOGGED_IN);
    } else {
      if (!mobileLayoutValue) {
        dispatch(metaActions.setCurrentMobileLayout('chat'));
      }
      removeExpert();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (phoneNumber) {
      fetchOrders(phoneNumber);
    }
  }, [phoneNumber, userNewOrderCount, fetchOrders]);

  useEffect(() => {
    if (logout) {
      deleteCookie(VISITOR_COOKIE_PARAMS.NAME);
    } else {
      dispatch(dialogsActions.setIsArchiveCurrentDialog(false));
    }
  }, [logout]);

  useEffect(() => {
    addMessagesSubscription();
    expertTypingSubscription();
    expertStatusChangedSubscription();
    messageChangedSubscription();
    listenPhoneRequestAuthFailed();
    listenUnreadCount();
    listenUserNewOrder();
  }, [
    addMessagesSubscription,
    expertTypingSubscription,
    expertStatusChangedSubscription,
    messageChangedSubscription,
    listenPhoneRequestAuthFailed,
    listenUnreadCount,
    listenUserNewOrder,
  ]);

  useEffect(() => {
    const localStore = storageCreator(() => localStorage);
    if (visitorId) {
      sendVisitorIdToHost(visitorId);
    }

    if (visitorId !== visitorIdCookie && visitorId && !logout) {
      setCookie(
        VISITOR_COOKIE_PARAMS.NAME,
        visitorId,
        VISITOR_COOKIE_PARAMS.EXPIRE,
        VISITOR_COOKIE_PARAMS.PATH,
        VISITOR_COOKIE_PARAMS.DOMAIN,
        VISITOR_COOKIE_PARAMS.SECURE,
        VISITOR_COOKIE_PARAMS.SAMESITE,
      );
      localStore.setItem('visitorId', visitorId);
    }
  }, [visitorIdCookie, visitorId, logout]);


  useEffect(() => {
    if (USE_YA) {
      addEcommerceSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const localStore = storageCreator(() => localStorage);
    const sessionStore = storageCreator(() => sessionStorage);
    const prevDialogId = localStore.getItem('dialogId');
    const activeDialogId = currentDialogId || dialogId;

    if (activeDialogId) {
      sessionStore.setItem('currentDialogId', `${activeDialogId}`);
    }

    if (activeDialogId && prevDialogId !== `${activeDialogId}`) {
      localStore.setItem('dialogId', activeDialogId);
    }
  }, [dialogId, currentDialogId]);

  return (
    <React.Fragment>
      {children}
      {USE_YA && (
        <YMInitializer
          accounts={[YANDEX_METRIC_ID]}
          options={{
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            trackHash: true,
            ecommerce: 'dataLayer',
          }}
          version="2"
        />
      )}
    </React.Fragment>
  );
};

export default connect(
  (state) => ({
    isSocketConnected: socketIsConnectedSelector(state),
  }),
  { ...expertsActions, ...messagesActions, ...metaActions, ...checkoutActions, ...dialogsActions },
)(SubscriptionsContainer);
